/*
eslint class-methods-use-this: ["error", {
  "exceptMethods": ["defineMethod", "camelize", "findObject"]
}]
*/

class StatusUtils {
  constructor(globalStatusList, statusMapping) {
    this.globalStatusList = globalStatusList;
    this.statusMapping = statusMapping;
    this.sortedStatusList = this.globalStatusList.sort((a, b) => {
      if (a.order === b.order) return 0;
      if (a.order === undefined) return 1;
      if (b.order === undefined) return -1;
      if (a.order > b.order) return 1;
      return -1;
    });
    this.injectHelpers();
  }

  injectHelpers() {
    const allStatusNames = this.globalStatusList.map(status => status.name);
    const mainStatusNames = this.mainStatusIds()
      .map(statusId => this.findObject(this.globalStatusList, { id: statusId }).name);

    allStatusNames.forEach((method) => {
      this.defineMethod(
        this.camelize(`is_${method}`),
        statusId => this.findObject(this.globalStatusList, { name: method }).id === statusId,
      );
    });

    mainStatusNames.forEach((method) => {
      this.defineMethod(
        this.camelize(`is_inside_${method}`),
        statusId => mainStatusNames.includes(statusId) ||
            this.statusMapping[this.findObject(this.globalStatusList, { name: method }).id]
              .includes(statusId),
      );
      this.defineMethod(
        this.camelize(`is_after_${method}`),
        (statusId) => {
          let targetStatusId;
          const mainStatusId = this.getMainStatusId(this.getStatusId(method));
          if (this.isMainStatus(statusId)) {
            targetStatusId = statusId;
          } else {
            targetStatusId = this.getMainStatusId(statusId);
          }
          const mainStatusIds = this.mainStatus.map(status => status.id);
          return mainStatusIds.indexOf(targetStatusId) > mainStatusIds.indexOf(mainStatusId);
        },
      );
      this.defineMethod(
        this.camelize(`is_before_${method}`),
        (statusId) => {
          let targetStatusId;
          const mainStatusId = this.getMainStatusId(this.getStatusId(method));
          if (this.isMainStatus(statusId)) {
            targetStatusId = statusId;
          } else {
            targetStatusId = this.getMainStatusId(statusId);
          }
          const mainStatusIds = this.mainStatus.map(status => status.id);
          return mainStatusIds.indexOf(targetStatusId) < mainStatusIds.indexOf(mainStatusId);
        },
      );
    });
  }

  getSubstatuses(statusId) {
    const substatusIds = this.statusMapping[statusId] || [];
    return this.globalStatusList.filter(status => substatusIds.includes(status.id));
  }

  getSubstatusIdsFromGroupIds(statusIds) {
    const result = statusIds.reduce((acc,status)=>{
      acc.push(...this.getSubstatusIds(status));
      return acc;
    },[]);
    return result;
  }

  getSubstatusIds(statusId) {
    const substatusIds = this.statusMapping[statusId] || [];
    return this.globalStatusList
      .filter(status => substatusIds.includes(status.id))
      .map(status => status.id);
  }
  getOrderSubstatusIds(statusId) {
    const substatusIds = statusId.map(status => this.statusMapping[status]) || [];
    const subStatus = substatusIds.filter((status) => { if (status && status.length) { return status; } });
    return this.globalStatusList
      .filter(status => subStatus.some(subStatusid => subStatusid.includes(status.id)))
      .map(status => status.id);
  }
  isMainStatusCheckings(statusId) {
    return this.mainStatusIds().some(status => statusId.includes(status));
  }
  isMainStatus(statusId) {
    return this.mainStatusIds().map(status => status).includes(statusId);
  }

  getStatusId(statusName) {
    return this.findObject(this.globalStatusList, { name: statusName }).id;
  }

  getStatusIds(statusNames) {
    return this.globalStatusList
      .filter(status => statusNames.includes(status.name))
      .map(status => status.id);
  }

  getStatusName(statusId) {
    return this.findObject(this.globalStatusList, { id: statusId }).name;
  }

  getStatusNames(statusIds) {
    return this.globalStatusList
      .filter(status => statusIds.includes(status.id))
      .map(status => status.name);
  }

  getMainStatusObj(statusId) {
    let mainStatusId;
    if (this.mainStatusIds().includes(statusId)) {
      mainStatusId = statusId;
    } else {
      this.mainStatusIds().forEach((status) => {
        if (this.statusMapping[status].includes(statusId)) {
          mainStatusId = status;
        }
      });
    }
    return this.findObject(this.globalStatusList, { id: mainStatusId });
  }

  getMainStatusName(statusId) {
    return this.getMainStatusObj(statusId) && this.getMainStatusObj(statusId).name;
  }

  getMainStatusLabel(statusId) {
    return this.getMainStatusObj(statusId).label;
  }

  getMainStatusId(statusId) {
    let mainStatusId;
    if (this.mainStatusIds().includes(statusId)) {
      mainStatusId = statusId;
    } else {
      this.mainStatusIds().forEach((status) => {
        if (this.statusMapping[status].includes(statusId)) {
          mainStatusId = status;
        }
      });
    }
    return mainStatusId;
  }

  mainStatusIds() {
    return Object.keys(this.statusMapping).map(status => parseInt(status, 10));
  }

  get mainStatus() {
    return Object.keys(this.statusMapping).map(statusId =>
      this.globalStatusList.find(status => status.id === parseInt(statusId, 10)));
  }

  get allStatusIds() {
    return this.globalStatusList.map(status => status.id);
  }

  subStatus(mainStatusId) {
    const substatusIds = this.statusMapping[mainStatusId];
    if (!substatusIds) {
      return [];
    }
    return this.globalStatusList.filter(status => substatusIds.includes(status.id));
  }

  defineMethod(name, func) {
    /* eslint no-param-reassign: ["error", { "props": false }] */
    StatusUtils.prototype[name] = func;
  }

  camelize(str) {
    return str.toLowerCase()
      .replace(/[-_]+/g, ' ')
      .replace(/ (.)/g, $1 => $1.toUpperCase())
      .replace(/ /g, '');
  }

  findObject(sourceArr, condition) {
    const key = Object.keys(condition)[0];
    const value = condition[key];
    const result = sourceArr.find(obj => obj[key] === value);
    return result;
  }

  isStatusGreaterThanGivenStatus(statusId, statusToCheckId) {
    const givenStatus = this.sortedStatusList.find(a => a.id === statusId);
    const expectedStatus = this.sortedStatusList.find(a => a.id === statusToCheckId);
    return givenStatus && expectedStatus && givenStatus.order > expectedStatus.order;
  }

  canCancelCourier(statusId) {
    const cancelExclusions = [
      this.getStatusId('delivery_pending'),
      this.getStatusId('delivered'),
      this.getStatusId('cancelled'),
    ];
    return !cancelExclusions.includes(statusId);
  }
}

module.exports = StatusUtils;
